import React from 'react';
import { motion } from 'framer-motion';
import { Users, Target, Award, Globe, BookOpen, TrendingUp } from 'lucide-react';

function AboutPage() {
  const stats = [
    { number: '0', label: 'Students Enrolled', icon: Users },
    { number: '1', label: 'Expert Instructor', icon: Award },
    { number: '4', label: 'Courses Available', icon: BookOpen },
    { number: 'New', label: 'Platform Status', icon: TrendingUp },
  ];

  const values = [
    {
      icon: Target,
      title: 'Excellence',
      description: 'We strive for the highest quality in every course we offer, ensuring our students receive world-class education.'
    },
    {
      icon: Users,
      title: 'Community',
      description: 'Building a supportive learning community where students can grow, collaborate, and achieve their goals together.'
    },
    {
      icon: Globe,
      title: 'Accessibility',
      description: 'Making premium education accessible to learners worldwide, regardless of their background or location.'
    },
    {
      icon: Award,
      title: 'Innovation',
      description: 'Continuously evolving our teaching methods and content to stay at the forefront of educational technology.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl font-bold text-slate-900 mb-6">
              About StartWithEnes
            </h1>
              <p className="text-xl text-slate-600 leading-relaxed">
                We're a new platform on a mission to democratize premium education and empower learners worldwide 
                to achieve their dreams through expert-led courses and innovative learning experiences.
              </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-3xl font-bold text-slate-900 mb-2">{stat.number}</h3>
                <p className="text-slate-600">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-slate-900 mb-6">Our Story</h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Founded in 2020 by Enes Uçar, StartWithEnes began as a vision to make high-quality 
                education accessible to everyone. What started as a small platform has grown into 
                a global community of learners and educators.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h3 className="text-2xl font-bold text-slate-900 mb-4">The Beginning</h3>
                <p className="text-slate-600 leading-relaxed mb-6">
                  After years of working in the tech industry and seeing the gap between 
                  traditional education and industry needs, Enes decided to create a platform 
                  that bridges this gap. The goal was simple: provide practical, 
                  industry-relevant education that actually prepares students for real-world challenges.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  We're just getting started and excited to build a community of learners 
                  with courses ranging from entrepreneurship to business development.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl p-8 text-white"
              >
                <h4 className="text-xl font-bold mb-4">Our Vision</h4>
                <p className="leading-relaxed">
                  "To create a world where anyone, anywhere, can access the education they need 
                  to transform their life and achieve their dreams."
                </p>
                <div className="mt-6 pt-6 border-t border-white/20">
                  <p className="font-semibold">- Enes Uçar, Founder & CEO</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-slate-900 mb-6">Our Values</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              These core values guide everything we do and shape the experience we create for our students.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">{value.title}</h3>
                <p className="text-slate-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Start Your Learning Journey?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Be among the first to join our new learning platform and transform your life with our courses.
            </p>
            <motion.a
              href="/courses"
              className="inline-block bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-colors duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Explore Our Courses
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default AboutPage;
